import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { Json, TablesInsert, TablesUpdate } from '../db';
import type { CategoryKind, ImportKind, InvoiceType, ItemStatus, MemberRole } from '../constants';
import type { StagedRow } from '../imports/mapping';
import * as acc from '../api/accounting';
import * as docsApi from '../api/documents';
import * as importsApi from '../api/imports';
import * as itemsApi from '../api/items';
import * as marketApi from '../api/market';
import * as platform from '../api/platform';
import * as pricingApi from '../api/pricing';
import * as stockApi from '../api/stock';
import { D, type Dec } from '../money';
import { todayIso } from '../format';
import { useAslen } from './provider';
import { qk } from './keys';

function useCompany() {
  const { client, companyId, member } = useAslen();
  return { client, companyId: companyId ?? '' , enabled: !!companyId, member };
}

// ── platform ─────────────────────────────────────────────────────────────────
export function useSettings() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.settings(companyId), queryFn: () => platform.getSettings(client, companyId), enabled });
}

export function useSetSetting() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: Json }) => platform.setSetting(client, companyId, key, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings(companyId) }),
  });
}

export function useMembers() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.members(companyId), queryFn: () => platform.listMembers(client, companyId), enabled });
}

/** Invitations addressed to the signed-in user (used before they belong to any company). */
export function useMyInvitations() {
  const { client, user } = useAslen();
  return useQuery({
    queryKey: qk.myInvitations(user?.id ?? null),
    queryFn: () => platform.myPendingInvitations(client),
    enabled: !!user,
  });
}

export function usePendingInvitations() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.invitations(companyId), queryFn: () => platform.listPendingInvitations(client, companyId), enabled });
}

export function useInviteMember(inviteUrlBase?: string) {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: MemberRole; displayName?: string }) => platform.inviteMember(client, companyId, input, inviteUrlBase),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.invitations(companyId) }),
  });
}

export function useUpdateMemberRole() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: MemberRole }) => platform.updateMemberRole(client, memberId, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(companyId) }),
  });
}

export function useCurrencies() {
  const { client } = useAslen();
  return useQuery({ queryKey: qk.currencies(), queryFn: () => platform.listCurrencies(client), staleTime: Infinity });
}

export function useMarketplaces() {
  const { client } = useAslen();
  return useQuery({ queryKey: qk.marketplaces(), queryFn: () => platform.listMarketplaces(client), staleTime: Infinity });
}

export function useUnits() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.units(companyId), queryFn: () => platform.listUnits(client, companyId), enabled });
}

export function useTaxRates() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.taxRates(companyId), queryFn: () => platform.listTaxRates(client, companyId), enabled });
}

export function useSequences() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.sequences(companyId), queryFn: () => platform.listNumberSequences(client, companyId), enabled });
}

export function useWarehouses() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.warehouses(companyId), queryFn: () => platform.listWarehouses(client, companyId), enabled });
}

export function useInfraBudget() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.infra(companyId), queryFn: () => platform.getInfraBudget(client, companyId), enabled, staleTime: 5 * 60_000 });
}

export function useAuditLog(limit = 100) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: [...qk.audit(companyId), limit], queryFn: () => platform.listAuditLog(client, companyId, limit), enabled });
}

export function useNotifications() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.notifications(companyId), queryFn: () => platform.listNotifications(client, companyId), enabled });
}

export function useMarkNotificationsRead() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => platform.markNotificationsRead(client, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications(companyId) }),
  });
}

export function useNotificationRules() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.notificationRules(companyId), queryFn: () => platform.listNotificationRules(client, companyId), enabled });
}

export function useUpdateNotificationRule() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { is_active?: boolean; params?: Json } }) => platform.updateNotificationRule(client, id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notificationRules(companyId) }),
  });
}

export function useUpdateCompany() {
  const { client, companyId } = useCompany();
  const { refreshMemberships } = useAslen();
  return useMutation({
    mutationFn: (patch: Parameters<typeof platform.updateCompany>[2]) => platform.updateCompany(client, companyId, patch),
    onSuccess: () => refreshMemberships(),
  });
}

// ── accounting ───────────────────────────────────────────────────────────────
export function useContacts() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.contacts(companyId), queryFn: () => acc.listContacts(client, companyId), enabled });
}

export function useOpenItems(contactId?: string) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.openItems(companyId, contactId),
    queryFn: () => acc.listOpenItems(client, companyId, contactId),
    enabled: enabled && contactId !== '',
  });
}

/** The contact's advances — receipts/payments with money still free to match (W-27). */
export function useOpenPayments(contactId?: string) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.openPayments(companyId, contactId),
    queryFn: () => acc.listOpenPayments(client, companyId, contactId),
    enabled: enabled && contactId !== '',
  });
}

/** Everything behind one contact's balance — the statement drawer (W-26) walks it. */
export function useContactStatement(contactId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.statement(companyId, contactId ?? ''),
    queryFn: () => acc.listContactStatement(client, companyId, contactId!),
    enabled: enabled && !!contactId,
  });
}

/**
 * ⌘K over the real data: contacts, invoices and transactions. The caller debounces
 * the term, and a term shorter than two characters asks nothing at all.
 */
export function useAccountingSearch(term: string, enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  const q = term.trim();
  return useQuery({
    queryKey: qk.search(companyId, q),
    queryFn: () => acc.searchAccounting(client, companyId, q),
    enabled: enabled && enabledWhen && q.length >= 2,
    staleTime: 30_000,
  });
}

export function useCategories(kind?: CategoryKind) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: [...qk.categories(companyId), kind ?? 'all'],
    queryFn: () => acc.listCategories(client, companyId, kind),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useUpsertCategory() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (row: Omit<TablesInsert<'expense_categories'>, 'company_id'>) => acc.upsertCategory(client, { ...row, company_id: companyId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.categories(companyId) }),
  });
}

export function useArchiveCategory() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => acc.archiveCategory(client, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.categories(companyId) }),
  });
}

export function useAccounts() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.accounts(companyId), queryFn: () => acc.listAccounts(client, companyId), enabled });
}

export function useLedger(accountId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.ledger(companyId, accountId ?? ''),
    queryFn: () => acc.listLedger(client, accountId!),
    enabled: enabled && !!accountId,
  });
}

export function useCompanyTransactions(from?: string, to?: string) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.transactions(companyId, from, to), queryFn: () => acc.listCompanyTransactions(client, companyId, from, to), enabled });
}

/** One transaction with its joins — what the ledger row hands the details drawer. */
export function useTransaction(id: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.transaction(companyId, id ?? ''), queryFn: () => acc.getTransaction(client, id!), enabled: enabled && !!id });
}

export function useInvoices(type: InvoiceType) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.invoices(companyId, type), queryFn: () => acc.listInvoices(client, companyId, type), enabled });
}

export function useInvoice(id: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.invoice(companyId, id ?? ''), queryFn: () => acc.getInvoice(client, id!), enabled: enabled && !!id });
}

export function usePartnerPositions() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.partners(companyId), queryFn: () => acc.listPartnerPositions(client, companyId), enabled });
}

export function useVatSummary(from: string, to: string) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.vat(companyId, from, to), queryFn: () => acc.vatSummary(client, companyId, from, to), enabled });
}

export function useCategoryTotals(from: string, to: string) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.categoryTotals(companyId, from, to), queryFn: () => acc.categoryTotals(client, companyId, from, to), enabled });
}

/**
 * The notional cost of the partners' money over a period (sermaye maliyeti).
 * It is not an expense anybody pays, so it never invalidates the money caches —
 * but a new reference rate changes it, which is why both live under ['acc', …].
 */
export function useCapitalCost(from: string, to: string) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.capitalCost(companyId, from, to), queryFn: () => acc.capitalCost(client, companyId, from, to), enabled });
}

export function useCapitalCostRates() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.capitalCostRates(companyId), queryFn: () => acc.listCapitalCostRates(client, companyId), enabled });
}

function useInvalidateCapitalCost() {
  const { companyId } = useCompany();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.capitalCostRates(companyId) });
    void qc.invalidateQueries({ queryKey: ['acc', companyId, 'capitalCost'] });
  };
}

export function useUpsertCapitalCostRate() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateCapitalCost();
  return useMutation({
    mutationFn: (row: Omit<TablesInsert<'capital_cost_rates'>, 'company_id'> & { id?: string }) =>
      acc.upsertCapitalCostRate(client, { ...row, company_id: companyId }),
    onSuccess: invalidate,
  });
}

export function useDeleteCapitalCostRate() {
  const { client } = useCompany();
  const invalidate = useInvalidateCapitalCost();
  return useMutation({ mutationFn: (id: string) => acc.deleteCapitalCostRate(client, id), onSuccess: invalidate });
}

/** Invalidates every accounting query of the company (balances depend on everything). */
function useInvalidateAccounting() {
  const { companyId } = useCompany();
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: qk.acc(companyId) });
}

export function useCreateTransaction() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({
    mutationFn: (t: Omit<acc.NewTransaction, 'companyId'>) => acc.createTransaction(client, { ...t, companyId }),
    onSuccess: invalidate,
  });
}

/** Corrects the fields that do not move money (description, category, contact, date, document). */
export function useUpdateTransaction() {
  const { client } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: acc.TransactionPatch }) => acc.updateTransaction(client, id, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteTransaction() {
  const { client } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({ mutationFn: (id: string) => acc.deleteTransaction(client, id), onSuccess: invalidate });
}

export function useSaveInvoice() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({
    mutationFn: ({ input, post }: { input: Omit<acc.SaveInvoiceInput, 'companyId'>; post: boolean }) =>
      acc.saveInvoice(client, { ...input, companyId }, post),
    onSuccess: invalidate,
  });
}

export function useCancelInvoice() {
  const { client } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({ mutationFn: (id: string) => acc.cancelInvoice(client, id), onSuccess: invalidate });
}

/**
 * Whether a purchase invoice's goods have been taken into stock. `movements === 0`
 * on an existing row means the receipt was posted and then fully reversed — not
 * the same thing as never received, which is a missing row.
 */
export function useInvoiceReceipt(invoiceId: string | null | undefined) {
  const { client, companyId } = useCompany();
  return useQuery({
    queryKey: qk.invoiceReceipt(companyId, invoiceId ?? ''),
    queryFn: () => acc.invoiceReceiptStatus(client, invoiceId!),
    enabled: !!invoiceId,
  });
}

/**
 * Mal kabul: the stock side of a purchase invoice. Invalidates three roots, not
 * one — the movement changes quantities (`stock`), the item status chips and
 * costs (`items`), and the invoice's own receipt badge (`acc`). Never optimistic:
 * the engine decides the cost, the lot number and the document number.
 */
export function useReceiveInvoice() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, locationId, occurredAt, note }: { invoiceId: string; locationId: string; occurredAt?: string | null; note?: string | null }) =>
      acc.receiveInvoice(client, invoiceId, locationId, { occurredAt, note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
      void qc.invalidateQueries({ queryKey: qk.items(companyId) });
      void qc.invalidateQueries({ queryKey: qk.acc(companyId) });
    },
  });
}

/** Reverses the whole receipt — the only way to undo one (`reverse_via_document`). */
export function useUnreceiveInvoice() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, note }: { invoiceId: string; note?: string | null }) => acc.unreceiveInvoice(client, invoiceId, note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
      void qc.invalidateQueries({ queryKey: qk.items(companyId) });
      void qc.invalidateQueries({ queryKey: qk.acc(companyId) });
    },
  });
}

/** Soft-deletes a draft — a posted invoice is cancelled instead, never removed. */
export function useDeleteDraftInvoice() {
  const { client } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({ mutationFn: (id: string) => acc.deleteDraftInvoice(client, id), onSuccess: invalidate });
}

/**
 * Matches a payment to an invoice after the fact. `amount` is in the invoice's
 * currency and `accountAmount` in the paying account's — pass both when they
 * differ, the database needs the second leg to book the kur farkı.
 */
export function useAllocatePayment() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({
    mutationFn: ({ transactionId, invoiceId, amount, accountAmount }: { transactionId: string; invoiceId: string; amount: string; accountAmount?: string }) =>
      acc.allocatePayment(client, companyId, transactionId, invoiceId, amount, accountAmount),
    onSuccess: invalidate,
  });
}

/** Undoes a match: the invoice reopens and the payment becomes an advance again. */
export function useRemoveAllocation() {
  const { client } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({ mutationFn: (id: string) => acc.removeAllocation(client, id), onSuccess: invalidate });
}

export function useAddInvoiceAttachment() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({
    mutationFn: ({ invoiceId, path, fileName, contentType }: { invoiceId: string; path: string; fileName: string; contentType?: string | null }) =>
      acc.addInvoiceAttachment(client, companyId, invoiceId, path, fileName, contentType),
    onSuccess: invalidate,
  });
}

export function useRemoveInvoiceAttachment() {
  const { client } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({ mutationFn: (id: string) => acc.removeInvoiceAttachment(client, id), onSuccess: invalidate });
}

export function useUpsertContact() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({
    mutationFn: (row: Omit<TablesInsert<'contacts'>, 'company_id'>) => acc.upsertContact(client, { ...row, company_id: companyId }),
    onSuccess: invalidate,
  });
}

export function useUpsertAccount() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({
    mutationFn: (row: Omit<TablesInsert<'cash_accounts'>, 'company_id'>) => acc.upsertAccount(client, { ...row, company_id: companyId }),
    onSuccess: invalidate,
  });
}

export function useUpdateAccount() {
  const { client } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TablesUpdate<'cash_accounts'> }) => acc.updateAccount(client, id, patch),
    onSuccess: invalidate,
  });
}

export function useUpdatePartnerShare() {
  const { client } = useCompany();
  const invalidate = useInvalidateAccounting();
  return useMutation({
    mutationFn: ({ contactId, sharePct }: { contactId: string; sharePct: string | null }) => acc.updatePartnerShare(client, contactId, sharePct),
    onSuccess: invalidate,
  });
}

// ── FX / reporting currency ─────────────────────────────────────────────────
/**
 * The market strip. Global data, so it does not wait for a company and is not
 * invalidated by anything the user does.
 *
 * The numbers change once a day, after TCMB publishes around 15:30 — polling
 * harder than that buys nothing. Half an hour of `staleTime` with an hourly
 * refetch means a tab left open overnight is current by morning without a
 * reload, and a tab opened at 15:25 picks the new day up by 16:25. The strip
 * never shows a spinner: while it is loading `data` is undefined and the
 * component renders nothing at all.
 */
export function useMarketTicker() {
  const { client } = useAslen();
  return useQuery({
    queryKey: qk.marketTicker(),
    queryFn: () => marketApi.listMarketTicker(client),
    staleTime: 30 * 60_000,
    refetchInterval: 60 * 60_000,
    // a decorative strip must not hammer a failing endpoint
    retry: 1,
  });
}

export function useFxRate(from: string, to: string, date: string = todayIso()) {
  const { client, companyId } = useAslen();
  return useQuery({
    queryKey: qk.fx(from, to, date),
    queryFn: () => acc.lookupFxRate(client, from, to, date, companyId ?? undefined),
    enabled: from !== to,
    staleTime: 30 * 60_000,
  });
}

/**
 * Global reporting currency toggle (₺/$/€). Amounts stored in base (TRY) are
 * divided by today's rate. Returns null while the rate is unknown.
 */
export function useReporting() {
  const { reportingCurrency, setReportingCurrency } = useAslen();
  const rate = useFxRate(reportingCurrency, 'TRY');
  const r = reportingCurrency === 'TRY' ? D(1) : rate.data ? D(rate.data.rate) : null;
  return {
    currency: reportingCurrency,
    setCurrency: setReportingCurrency,
    rate: r,
    rateSource: rate.data?.source ?? null,
    fromBase: (amountBase: number | string | null | undefined): Dec | null => (r ? D(amountBase).dividedBy(r) : null),
  };
}

// ── items & product trees (M1) ──────────────────────────────────────────────
/**
 * Two roots have to move together after an item master write: `items` (the list,
 * the card, the trees) and `stock` (the on-hand views join item names, units and
 * the stock status, which `critical_ratio` and `reorder_point` feed).
 */
function useInvalidateItems() {
  const { companyId } = useCompany();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.items(companyId) });
    void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
  };
}

export function useItems(filters: itemsApi.ItemListFilters = {}) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.itemList(companyId, filters),
    queryFn: () => itemsApi.listItems(client, companyId, filters),
    enabled,
  });
}

/** The command palette's product results; same source as W-04, capped and debounced by the caller. */
export function useItemSearch(term: string, enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  const q = term.trim();
  return useQuery({
    queryKey: qk.itemList(companyId, { search: q, limit: 6 }),
    queryFn: () => itemsApi.listItems(client, companyId, { search: q, limit: 6 }),
    enabled: enabled && enabledWhen && q.length >= 2,
    staleTime: 30_000,
  });
}

/** The W-05 card: the row with its UoM ladder and its group. */
export function useItem(id: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.item(companyId, id ?? ''), queryFn: () => itemsApi.getItem(client, id!), enabled: enabled && !!id });
}

/** One `v_items_list` row — what a scan or the mobile item card resolves to. */
export function useItemListRow(id: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: [...qk.item(companyId, id ?? ''), 'listRow'],
    queryFn: () => itemsApi.getItemListRow(client, companyId, id!),
    enabled: enabled && !!id,
  });
}

export function useItemUoms(itemId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.itemUoms(companyId, itemId ?? ''),
    queryFn: () => itemsApi.listItemUoms(client, itemId!),
    enabled: enabled && !!itemId,
  });
}

/** Settings → Birimler → "Ürüne özel dönüşümler". */
export function useItemUomOverrides() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.itemUomOverrides(companyId), queryFn: () => itemsApi.listItemUomOverrides(client, companyId), enabled });
}

export function useItemGroups() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.itemGroups(companyId),
    queryFn: () => itemsApi.listItemGroups(client, companyId),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useWhereUsed(itemId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.whereUsed(companyId, itemId ?? ''),
    queryFn: () => itemsApi.listWhereUsed(client, itemId!),
    enabled: enabled && !!itemId,
  });
}

export function useItemBoms(itemId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.boms(companyId, itemId ?? ''), queryFn: () => itemsApi.listItemBoms(client, itemId!), enabled: enabled && !!itemId });
}

export function useBom(bomId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.bom(companyId, bomId ?? ''), queryFn: () => itemsApi.getBom(client, bomId!), enabled: enabled && !!bomId });
}

export function useBomTree(bomId: string | null, maxDepth?: number) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: [...qk.bomTree(companyId, bomId ?? ''), maxDepth ?? null],
    queryFn: () => itemsApi.bomTree(client, bomId!, maxDepth),
    enabled: enabled && !!bomId,
  });
}

/**
 * The BOM cost panel. Gate the call on `can(role, 'seesCosts')` as well — the
 * function raises 42501 for anyone else, and an error state where the panel
 * should simply be absent is the wrong thing to show.
 */
export function useBomCost(bomId: string | null, enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.bomCost(companyId, bomId ?? ''),
    queryFn: () => itemsApi.bomCost(client, bomId!),
    enabled: enabled && enabledWhen && !!bomId,
  });
}

/** W-05 → Maliyet. Same role gate as `useBomCost`. */
export function useItemCostCard(itemId: string | null, enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.itemCostCard(companyId, itemId ?? ''),
    queryFn: () => itemsApi.itemCostCard(client, itemId!),
    enabled: enabled && enabledWhen && !!itemId,
  });
}

/**
 * W-05 → Stok, left card. Lives under the `stock` root, so a posted movement
 * refreshes the item card without the item root being touched.
 */
export function useItemStockByLocation(itemId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.itemStock(companyId, itemId ?? ''),
    queryFn: () => itemsApi.listItemStockByLocation(client, itemId!),
    enabled: enabled && !!itemId,
  });
}

/** W-05 → Stok, right card (lot × location). */
export function useItemLots(itemId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.itemLots(companyId, itemId ?? ''), queryFn: () => itemsApi.listItemLots(client, itemId!), enabled: enabled && !!itemId });
}

/** W-05 → Hareketler: the item-scoped slice of the W-07 ledger. */
export function useItemMovements(itemId: string | null, limit = 50) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: [...qk.itemMovements(companyId, itemId ?? ''), limit],
    queryFn: () => itemsApi.listItemMovements(client, itemId!, limit),
    enabled: enabled && !!itemId,
  });
}

// ── documents ───────────────────────────────────────────────────────────────
/** Attachments of any record ('item', 'lot', 'bom', …) — W-05 → Belgeler. */
export function useDocuments(refType: string, refId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.documents(companyId, refType, refId ?? ''),
    queryFn: () => docsApi.listDocuments(client, refType, refId!),
    enabled: enabled && !!refId,
  });
}

export function useUploadDocument(refType: string, refId: string) {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<docsApi.UploadDocumentInput, 'companyId' | 'refType' | 'refId'>) =>
      docsApi.uploadDocument(client, { ...input, companyId, refType, refId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.documents(companyId, refType, refId) }),
  });
}

export function useArchiveDocument(refType: string, refId: string) {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => docsApi.archiveDocument(client, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.documents(companyId, refType, refId) }),
  });
}

// ── the company drawer (şirket evrak arşivi) ────────────────────────────────
/**
 * The nine seeded dividers plus whatever the owner added. Gate the call on
 * `can(role, 'accountingRead')`: the select policy refuses an operator, and an
 * error toast over an empty cabinet reads like a bug.
 */
export function useDocumentFolders(enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.documentFolders(companyId),
    queryFn: () => docsApi.listDocumentFolders(client, companyId),
    enabled: enabled && enabledWhen,
  });
}

export function useSaveDocumentFolder() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<docsApi.SaveDocumentFolderInput, 'companyId'>) => docsApi.saveDocumentFolder(client, { ...input, companyId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.documentFolders(companyId) }),
  });
}

export function useArchiveDocumentFolder() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => docsApi.archiveDocumentFolder(client, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.documentFolders(companyId) }),
  });
}

/** Every company paper in one request; the screen groups them by folder. */
export function useCompanyDocuments(enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.companyDocuments(companyId),
    queryFn: () => docsApi.listCompanyDocuments(client, companyId),
    enabled: enabled && enabledWhen,
  });
}

export function useUploadCompanyDocument() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<docsApi.UploadDocumentInput, 'companyId' | 'refType' | 'refId'>) =>
      docsApi.uploadDocument(client, { ...input, companyId, refType: docsApi.COMPANY_DOC_REF, refId: companyId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.companyDocuments(companyId) }),
  });
}

/**
 * Retitling a paper, moving it between dividers, giving it an expiry date. The
 * caller holds one document and not necessarily its `ref_type`, so both roots are
 * invalidated: the drawer, and every per-record attachment list.
 */
export function useUpdateDocument() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: docsApi.DocumentPatch }) => docsApi.updateDocument(client, id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.docs(companyId) });
      void qc.invalidateQueries({ queryKey: qk.documentRefs(companyId) });
    },
  });
}

/** Archive, not delete — see `archiveDocument`. */
export function useArchiveCompanyDocument() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => docsApi.archiveDocument(client, id),
    onSuccess: () => {
      // a folder that has just lost its last paper may now be archivable
      void qc.invalidateQueries({ queryKey: qk.docs(companyId) });
    },
  });
}

export function useSaveItem() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: (input: Omit<itemsApi.SaveItemInput, 'companyId'>) => itemsApi.saveItem(client, { ...input, companyId }),
    onSuccess: invalidate,
  });
}

export function useSetItemsStatus() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: ItemStatus }) => itemsApi.setItemsStatus(client, ids, status),
    onSuccess: invalidate,
  });
}

export function useBulkPatchItemAttributes() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: ({ ids, patch, itemGroupId }: { ids: string[]; patch: Json; itemGroupId?: string | null }) =>
      itemsApi.bulkPatchItemAttributes(client, ids, patch, itemGroupId),
    onSuccess: invalidate,
  });
}

export function useSetItemBarcode() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: ({ itemId, code, unit }: { itemId: string; code: string; unit?: string | null }) => itemsApi.setItemBarcode(client, itemId, code, unit),
    onSuccess: invalidate,
  });
}

export function useSaveItemGroup() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: (row: Omit<TablesInsert<'item_groups'>, 'company_id'>) => itemsApi.upsertItemGroup(client, { ...row, company_id: companyId }),
    onSuccess: invalidate,
  });
}

export function useArchiveItemGroup() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({ mutationFn: (id: string) => itemsApi.archiveItemGroup(client, id), onSuccess: invalidate });
}

export function useCreateBomVersion() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: ({ itemId, fromBomId }: { itemId: string; fromBomId?: string | null }) => itemsApi.createBomVersion(client, itemId, fromBomId),
    onSuccess: invalidate,
  });
}

export function useSaveBomDraft() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: ({ bomId, draft }: { bomId: string; draft: itemsApi.BomDraftInput }) => itemsApi.saveBomDraft(client, bomId, draft),
    onSuccess: invalidate,
  });
}

export function useActivateBom() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: ({ bomId, changeNote, updateStandard }: { bomId: string; changeNote: string; updateStandard?: boolean }) =>
      itemsApi.activateBom(client, bomId, changeNote, updateStandard),
    onSuccess: invalidate,
  });
}

export function useCopyBom() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: ({ sourceBomId, targetItemId, swapVariants }: { sourceBomId: string; targetItemId: string; swapVariants?: boolean }) =>
      itemsApi.copyBom(client, sourceBomId, targetItemId, swapVariants),
    onSuccess: invalidate,
  });
}

export function useDeleteBomDraft() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({ mutationFn: (bomId: string) => itemsApi.deleteBomDraft(client, bomId), onSuccess: invalidate });
}

export function useRecomputeStandardCost() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: ({ itemId, note }: { itemId: string; note?: string | null }) => itemsApi.recomputeStandardCost(client, itemId, note),
    onSuccess: invalidate,
  });
}

export function useSetStandardCost() {
  const { client } = useCompany();
  const invalidate = useInvalidateItems();
  return useMutation({
    mutationFn: ({ itemId, cost, unit, note }: { itemId: string; cost: string; unit?: string | null; note?: string | null }) =>
      itemsApi.setStandardCost(client, itemId, cost, unit, note),
    onSuccess: invalidate,
  });
}

// ── pazaryeri fiyatlaması (W-05 › Satış fiyatı) ─────────────────────────────
/**
 * Satış kanalları. Returns `[]` both when the migration has not been pushed and
 * when RLS refuses the reader — the screen tells those apart by the role it
 * already has, and falls back to `CHANNEL_DEFAULTS` only in the first case.
 */
export function useSalesChannels() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.salesChannels(companyId), queryFn: () => pricingApi.listSalesChannels(client, companyId), enabled });
}

/** Every listing of one item, with its channel's terms already resolved. */
export function useChannelListings(itemId: string | null, enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.channelListings(companyId, itemId ?? ''),
    queryFn: () => pricingApi.listChannelListings(client, companyId, itemId!),
    enabled: enabled && enabledWhen && !!itemId,
  });
}

export function useSaveChannelListing() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<pricingApi.ChannelListingInput, 'company_id'>) =>
      pricingApi.saveChannelListing(client, { ...input, company_id: companyId }),
    // the listings of the item that was touched, and nothing else: a price is not a
    // quantity and must not make the stock screens refetch
    onSuccess: (_id, input) => qc.invalidateQueries({ queryKey: qk.channelListings(companyId, input.item_id) }),
  });
}

export function useArchiveChannelListing() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; itemId: string }) => pricingApi.archiveChannelListing(client, id),
    onSuccess: (_v, { itemId }) => qc.invalidateQueries({ queryKey: qk.channelListings(companyId, itemId) }),
  });
}

/**
 * Channel-wide terms. A changed commission moves every listing on that channel, so
 * this invalidates the whole items root rather than one item's listings.
 */
export function useSaveSalesChannel() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<pricingApi.SalesChannel> }) => pricingApi.saveSalesChannel(client, id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.items(companyId) }),
  });
}

// ── stock reference & settings (M2, W-31) ───────────────────────────────────
/** Warehouses with their zones and locations — the whole Depolar section in one query set. */
export function useWarehousesFull() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.warehousesFull(companyId), queryFn: () => stockApi.listWarehouseTree(client, companyId), enabled });
}

/**
 * W-31a detail view: what sits in each location, which count last touched it and
 * whether the warehouse ever moved stock (the guards the drawers obey).
 */
export function useWarehouseFacts(warehouseId: string | undefined) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.warehouseFacts(companyId, warehouseId ?? 'none'),
    queryFn: () => stockApi.getWarehouseFacts(client, companyId, warehouseId as string),
    enabled: enabled && !!warehouseId,
  });
}

/** W-31b: the opening count sessions and movements the lock dialog re-writes. */
export function useOpeningStockSummary(enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.openingStock(companyId),
    queryFn: () => stockApi.getOpeningStockSummary(client, companyId),
    enabled: enabled && enabledWhen,
  });
}

/**
 * How often each unit is referenced — what disables the "Durum" toggle in
 * Ayarlar › Birimler before the SQL guard has to refuse the write.
 */
export function useUnitUsage() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.unitUsage(companyId), queryFn: () => stockApi.listUnitUsage(client, companyId), enabled });
}

export function useWorkCenters() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.workCenters(companyId), queryFn: () => stockApi.listWorkCenters(client, companyId), enabled });
}

/** `settings.stock`, `costing`, `item_defaults`, `labels`, `mobile`, … in one read. */
export function useStockSettings() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.stockSettings(companyId), queryFn: () => stockApi.getStockSettings(client, companyId), enabled });
}

export function useStockKpis() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.stockKpis(companyId), queryFn: () => stockApi.getStockKpis(client, companyId), enabled });
}

// ── on-hand matrix (W-06) ───────────────────────────────────────────────────
/** The item rows of W-06: totals, status, and the stock value cost roles may see. */
export function useStockItemSummary() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.stockSummary(companyId), queryFn: () => stockApi.listStockItemSummary(client, companyId), enabled });
}

/** The cells of W-06: item × zone, one query for the whole company. */
export function useStockMatrix() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.stockMatrix(companyId), queryFn: () => stockApi.listStockMatrix(client, companyId), enabled });
}

// ── movement ledger and posting (W-07) ──────────────────────────────────────
/** The company's reason codes: the form's chips and the ledger's "Sebep" menu. */
export function useStockReasonCodes() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.stockReasonCodes(companyId), queryFn: () => stockApi.listReasonCodes(client, companyId), enabled, staleTime: 5 * 60_000 });
}

/**
 * W-07, page by page. The key carries the filters, so a chip change is a new
 * query instead of a refetch of the old one, and `['stock', company]` still
 * invalidates every page after a movement is posted.
 */
export function useStockLedger(filters: stockApi.LedgerFilters) {
  const { client, companyId, enabled } = useCompany();
  return useInfiniteQuery({
    queryKey: qk.stockLedger(companyId, filters),
    queryFn: ({ pageParam }) => stockApi.listStockLedger(client, companyId, filters, pageParam),
    initialPageParam: null as stockApi.LedgerCursor | null,
    getNextPageParam: (last: stockApi.LedgerPage) => last.next,
    enabled,
  });
}

/** One movement by its number — the read-only detail drawer behind `?m=`. */
export function useStockMovement(no: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.stockMovement(companyId, no ?? ''),
    queryFn: () => stockApi.getMovementByNo(client, companyId, no as string),
    enabled: enabled && !!no,
  });
}

/**
 * The next document number without consuming it ("HRK-2026-01285 · taslak").
 * Never cached beyond the drawer: someone else's movement takes the number, and a
 * stale draft number in a subtitle is a small lie the toast would then contradict.
 */
export function usePeekDocumentNo(docType = 'stock_movement', enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.documentNo(companyId, docType),
    queryFn: () => stockApi.peekDocumentNo(client, companyId, docType),
    enabled: enabled && enabledWhen,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Posting a movement (G7: never optimistic — the engine numbers, costs and
 * guards the row, and a balance drawn before it answered would be a guess). Every
 * stock query is invalidated on success, plus the item root for the status chips
 * `v_items_list` carries.
 */
export function usePostStockMovement() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<stockApi.PostMovementInput, 'companyId'>) => stockApi.postStockMovement(client, { ...input, companyId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
      void qc.invalidateQueries({ queryKey: qk.items(companyId) });
    },
  });
}

/** "Geri al" (creator, inside the window) and a partner's reversal after it. */
export function useReverseStockMovement() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string | null }) => stockApi.reverseStockMovement(client, id, note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
      void qc.invalidateQueries({ queryKey: qk.items(companyId) });
    },
  });
}

// ── lots and traceability (W-08) ────────────────────────────────────────────
/**
 * Every lot of the company, newest receipt first — the W-08a browser filters,
 * searches and sorts these rows in the browser, the way W-04 and W-06 do. The
 * view carries no cost column, so this query is safe for every stock role.
 */
export function useLots() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.lots(companyId), queryFn: () => stockApi.listLots(client, companyId), enabled });
}

/**
 * The unit costs the "Birim maliyet" column joins in. Asked for only when the
 * role sees costs: `lot_costs` is a separate table precisely so RLS can keep it
 * from the operator, and a query that comes back empty must not read as "0 ₺".
 */
export function useLotCosts(enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.lotCosts(companyId), queryFn: () => stockApi.listLotCosts(client, companyId), enabled: enabled && enabledWhen });
}

/** One lot by its number — the W-08 page behind `/stock/lots/$lotNo`. */
export function useLot(lotNo: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.lot(companyId, lotNo ?? ''),
    queryFn: () => stockApi.getLotByNo(client, companyId, lotNo as string),
    enabled: enabled && !!lotNo,
  });
}

/** The genealogy of one lot in one direction (`?dir=`), as `fn_lot_trace` answers it. */
export function useLotTrace(lotId: string | null, direction: stockApi.TraceDirection = 'both') {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.lotTrace(companyId, lotId ?? '', direction),
    queryFn: () => stockApi.getLotTrace(client, lotId as string, direction),
    enabled: enabled && !!lotId,
  });
}

/** The selected node's side panel (§4.7). */
export function useTraceNodeDetail(kind: string | null, refId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.traceNode(companyId, kind ?? '', refId ?? ''),
    queryFn: () => stockApi.getTraceNodeDetail(client, kind as string, refId as string),
    enabled: enabled && !!kind && !!refId,
  });
}

/**
 * Lot maintenance (G3): expiry, the supplier's batch number, and block / unblock.
 * Never optimistic — the same rule posting follows (G7). Quarantine decides
 * whether the engine will let the lot out at all, so the screen waits for the
 * server's answer instead of showing a state the database may refuse.
 */
export function useUpdateLot() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lotId, patch }: { lotId: string; patch: Record<string, Json> }) => stockApi.updateLot(client, lotId, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
    },
  });
}

// ── counts (W-09) ───────────────────────────────────────────────────────────
/**
 * Every count session of the company, newest first — the W-09a list filters and
 * searches these rows in the browser, the way the lot browser does. The view
 * masks `value_impact_base` for a role without `seesCosts`, so one query serves
 * every stock role.
 */
export function useStockCounts() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({ queryKey: qk.counts(companyId), queryFn: () => stockApi.listStockCounts(client, companyId), enabled });
}

/** One session by its number — what the W-09 route resolves before it asks for the review. */
export function useStockCount(countNo: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.count(companyId, countNo ?? ''),
    queryFn: () => stockApi.getStockCountByNo(client, companyId, countNo as string),
    enabled: enabled && !!countNo,
  });
}

/**
 * The review payload behind `/stock/counts/$countNo`. Kept fresh rather than
 * cached: the phone is still writing lines into a session that is being reviewed,
 * and `review_version` is what the approval is checked against.
 */
export function useStockCountReview(countId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.countReview(companyId, countId ?? ''),
    queryFn: () => stockApi.getStockCountReview(client, countId as string),
    enabled: enabled && !!countId,
    staleTime: 0,
  });
}

/**
 * M-05: the session the phone counts from. Kept out of the cache's normal
 * staleness rules — the device's own count store is the source of truth while the
 * walk is running, and this query is what a reconnect or a pull-to-refresh merges
 * into it (`mergeCountSession`).
 */
export function useCountSession(countId: string | null, enabledWhen = true) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.countSession(companyId, countId ?? ''),
    queryFn: () => stockApi.getCountSession(client, countId as string),
    enabled: enabled && !!countId && enabledWhen,
    staleTime: 0,
  });
}

/** "Sayım başlat" (§5.1). Invalidates the list; the caller navigates to the new session. */
export function useStartStockCount() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: stockApi.StartCountInput) => stockApi.startStockCount(client, companyId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.counts(companyId) });
    },
  });
}

/**
 * The approval (G7: never optimistic). Every stock query is invalidated on
 * success — the movements it posted change the matrix, the ledger, the lots and
 * the item cards — plus the item root for the status chips `v_items_list` carries.
 */
export function useApproveStockCount() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { countId: string; lineIds: string[]; reviewVersion?: number | null; note?: string | null }) =>
      stockApi.approveStockCount(client, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
      void qc.invalidateQueries({ queryKey: qk.items(companyId) });
    },
  });
}

/** "Yeniden saydır" (§6.8). Posts nothing, so only the count queries change. */
export function useRequestCountRecount() {
  const { client, companyId } = useCompany();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { countId: string; lineIds: string[]; note?: string | null }) => stockApi.requestCountRecount(client, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.counts(companyId) });
      void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
    },
  });
}

export function useImportPrerequisites() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.importPrerequisites(companyId),
    queryFn: () => stockApi.getImportPrerequisites(client, companyId),
    enabled,
  });
}

/** A unit rename reaches every item row and every quantity label, so items move too. */
function useInvalidateUnits() {
  const { companyId } = useCompany();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.units(companyId) });
    void qc.invalidateQueries({ queryKey: qk.items(companyId) });
  };
}

export function useUpsertUnit() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateUnits();
  return useMutation({
    mutationFn: (row: Omit<TablesInsert<'units'>, 'company_id'>) => stockApi.upsertUnit(client, { ...row, company_id: companyId }),
    onSuccess: invalidate,
  });
}

export function useUpdateUnit() {
  const { client } = useCompany();
  const invalidate = useInvalidateUnits();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TablesUpdate<'units'> }) => platform.updateUnit(client, id, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteUnit() {
  const { client } = useCompany();
  const invalidate = useInvalidateUnits();
  return useMutation({ mutationFn: (id: string) => stockApi.deleteUnit(client, id), onSuccess: invalidate });
}

/** A warehouse, zone or location write moves both the warehouse cache and every stock view. */
function useInvalidateWarehouses() {
  const { companyId } = useCompany();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.warehouses(companyId) });
    void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
  };
}

export function useUpsertWarehouse() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateWarehouses();
  return useMutation({
    mutationFn: (row: Omit<TablesInsert<'warehouses'>, 'company_id'>) => stockApi.upsertWarehouse(client, { ...row, company_id: companyId }),
    onSuccess: invalidate,
  });
}

export function useUpdateWarehouse() {
  const { client } = useCompany();
  const invalidate = useInvalidateWarehouses();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TablesUpdate<'warehouses'> }) => stockApi.updateWarehouse(client, id, patch),
    onSuccess: invalidate,
  });
}

export function useUpsertZone() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateWarehouses();
  return useMutation({
    mutationFn: (row: Omit<TablesInsert<'warehouse_zones'>, 'company_id'>) => stockApi.upsertZone(client, { ...row, company_id: companyId }),
    onSuccess: invalidate,
  });
}

export function useArchiveZone() {
  const { client } = useCompany();
  const invalidate = useInvalidateWarehouses();
  return useMutation({ mutationFn: (id: string) => stockApi.archiveZone(client, id), onSuccess: invalidate });
}

export function useUpsertLocation() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateWarehouses();
  return useMutation({
    mutationFn: (row: Omit<TablesInsert<'locations'>, 'company_id'>) => stockApi.upsertLocation(client, { ...row, company_id: companyId }),
    onSuccess: invalidate,
  });
}

export function useUpdateLocation() {
  const { client } = useCompany();
  const invalidate = useInvalidateWarehouses();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TablesUpdate<'locations'> }) => stockApi.updateLocation(client, id, patch),
    onSuccess: invalidate,
  });
}

export function useArchiveLocation() {
  const { client } = useCompany();
  const invalidate = useInvalidateWarehouses();
  return useMutation({ mutationFn: (id: string) => stockApi.archiveLocation(client, id), onSuccess: invalidate });
}

export function useCreateLocationsBulk() {
  const { client } = useCompany();
  const invalidate = useInvalidateWarehouses();
  return useMutation({ mutationFn: (input: stockApi.BulkLocationsInput) => stockApi.createLocationsBulk(client, input), onSuccess: invalidate });
}

/** Work-centre rates feed `fn_bom_cost`, so a rate change invalidates the item trees too. */
function useInvalidateWorkCenters() {
  const { companyId } = useCompany();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.workCenters(companyId) });
    void qc.invalidateQueries({ queryKey: qk.items(companyId) });
  };
}

export function useUpsertWorkCenter() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateWorkCenters();
  return useMutation({
    mutationFn: (row: Omit<TablesInsert<'work_centers'>, 'company_id'>) => stockApi.upsertWorkCenter(client, { ...row, company_id: companyId }),
    onSuccess: invalidate,
  });
}

export function useArchiveWorkCenter() {
  const { client } = useCompany();
  const invalidate = useInvalidateWorkCenters();
  return useMutation({ mutationFn: (id: string) => stockApi.archiveWorkCenter(client, id), onSuccess: invalidate });
}

export function useSaveWorkCenterRates() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateWorkCenters();
  return useMutation({
    mutationFn: ({ workCenterId, rates }: { workCenterId: string; rates: stockApi.WorkCenterRateInput }) =>
      stockApi.saveWorkCenterRates(client, companyId, workCenterId, rates),
    onSuccess: invalidate,
  });
}

/**
 * Settings live in two caches — `qk.settings` (the whole map, read by the rest of
 * the app) and `qk.stockSettings` (this section's slice) — and `critical_ratio`
 * decides `v_items_list.stock_status`, so all three move together.
 */
function useInvalidateStockSettings() {
  const { companyId } = useCompany();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.settings(companyId) });
    void qc.invalidateQueries({ queryKey: qk.stockSettings(companyId) });
    void qc.invalidateQueries({ queryKey: qk.items(companyId) });
  };
}

export function usePatchStockSetting() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateStockSettings();
  return useMutation({
    mutationFn: ({ key, patch }: { key: stockApi.StockSettingKey; patch: stockApi.SettingValue }) =>
      stockApi.patchStockSetting(client, companyId, key, patch),
    onSuccess: invalidate,
  });
}

/** Owner-only (E12). Locking also closes the opening import, hence the stock root. */
export function useLockOpeningStock() {
  const { client, companyId, member } = useCompany();
  const qc = useQueryClient();
  const invalidate = useInvalidateStockSettings();
  return useMutation({
    mutationFn: (at?: string) => stockApi.lockOpeningStock(client, companyId, at, member?.user_id ?? null),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
    },
  });
}

// ── import wizard (W-31c / W-31d) ───────────────────────────────────────────
/** "Yükleme geçmişi" — one kind for the wizard sidebar, all of them in settings. */
export function useImportBatches(kind?: ImportKind, limit = 20) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.importBatches(companyId, kind ?? 'all'),
    queryFn: () => importsApi.listImportBatches(client, companyId, kind, limit),
    enabled,
  });
}

export function useImportBatch(batchId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.importBatch(companyId, batchId ?? 'none'),
    queryFn: () => importsApi.getImportBatch(client, companyId, batchId as string),
    enabled: enabled && !!batchId,
  });
}

/**
 * W-31c: lot tracking, missing stock-unit conversions and missing standard costs
 * of this company's items — the checklist rows the design draws and the standard
 * cost the "Değer" column falls back to.
 */
export function useImportItemFacts() {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.importItemFacts(companyId),
    queryFn: () => importsApi.getImportItemFacts(client, companyId),
    enabled,
  });
}

/** The staged rows of a batch reopened from the history (`raw` included). */
export function useImportRows(batchId: string | null) {
  const { client, companyId, enabled } = useCompany();
  return useQuery({
    queryKey: qk.importRows(companyId, batchId ?? 'none'),
    queryFn: () => importsApi.listImportRows(client, batchId as string),
    enabled: enabled && !!batchId,
  });
}

/**
 * A posted or reverted batch has moved stock, created lots and possibly items,
 * so it invalidates both quantity roots — not just the wizard's own.
 */
function useInvalidateImports() {
  const { companyId } = useCompany();
  const qc = useQueryClient();
  return (touchedStock = false) => {
    void qc.invalidateQueries({ queryKey: qk.imports(companyId) });
    if (touchedStock) {
      void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
      void qc.invalidateQueries({ queryKey: qk.items(companyId) });
    }
  };
}

export function useStageImport() {
  const { client, companyId } = useCompany();
  const invalidate = useInvalidateImports();
  return useMutation({
    mutationFn: ({ kind, file, options }: { kind: ImportKind; file: importsApi.StageImportFile; options?: Record<string, Json> }) =>
      importsApi.stageImport(client, companyId, kind, file, options),
    onSuccess: () => invalidate(),
  });
}

export function useAppendImportRows() {
  const { client } = useCompany();
  return useMutation({
    mutationFn: ({ batchId, rows, onProgress }: { batchId: string; rows: StagedRow[]; onProgress?: (done: number, total: number) => void }) =>
      importsApi.appendImportRows(client, batchId, rows, onProgress),
  });
}

export function useValidateImport() {
  const { client } = useCompany();
  const invalidate = useInvalidateImports();
  return useMutation({
    mutationFn: ({ batchId, options }: { batchId: string; options?: Record<string, Json> }) =>
      importsApi.validateImport(client, batchId, options),
    onSuccess: () => invalidate(),
  });
}

export function usePostImport() {
  const { client } = useCompany();
  const invalidate = useInvalidateImports();
  return useMutation({
    mutationFn: ({ batchId, options }: { batchId: string; options?: Parameters<typeof importsApi.postImport>[2] }) =>
      importsApi.postImport(client, batchId, options),
    onSuccess: () => invalidate(true),
  });
}

export function useRevertImport() {
  const { client } = useCompany();
  const invalidate = useInvalidateImports();
  return useMutation({
    mutationFn: (batchId: string) => importsApi.revertImport(client, batchId),
    onSuccess: () => invalidate(true),
  });
}

export function useDiscardImport() {
  const { client } = useCompany();
  const invalidate = useInvalidateImports();
  return useMutation({
    mutationFn: (batchId: string) => importsApi.discardImport(client, batchId),
    onSuccess: () => invalidate(),
  });
}

// ── realtime ────────────────────────────────────────────────────────────────
/** Invalidates accounting queries when the partner changes something (Supabase Realtime). */
export function useRealtimeAccounting() {
  const { client, companyId } = useAslen();
  const qc = useQueryClient();
  useEffect(() => {
    if (!companyId) return;
    const channel = client
      .channel(`acc-${companyId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'transactions', filter: `company_id=eq.${companyId}` }, () =>
        qc.invalidateQueries({ queryKey: qk.acc(companyId) }),
      )
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invoices', filter: `company_id=eq.${companyId}` }, () =>
        qc.invalidateQueries({ queryKey: qk.acc(companyId) }),
      )
      .on('postgres_changes', { event: '*', schema: 'public', table: 'contacts', filter: `company_id=eq.${companyId}` }, () =>
        qc.invalidateQueries({ queryKey: qk.contacts(companyId) }),
      )
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications', filter: `company_id=eq.${companyId}` }, () =>
        qc.invalidateQueries({ queryKey: qk.notifications(companyId) }),
      )
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [client, companyId, qc]);
}

/**
 * The item-master twin of `useRealtimeAccounting`: one channel for the tables
 * migration 20260923000007 publishes on the M1 side. Mount it next to the
 * accounting one in the web `AppShell`; mobile has no realtime subscription.
 * The stock tables (`stock_balances`, `stock_movements`, counts, lots, labels)
 * belong to the movement spec's own `useRealtimeStock`.
 */
/** What a movement posted by the *other* partner tells a screen (spec §1.7). */
export interface StockMovementEvent {
  movementId: string | null;
  itemId: string | null;
  actorId: string | null;
  no: string | null;
}

/**
 * Stock realtime: one channel per company on the two tables migration
 * 20260923000003 publishes. Every event invalidates the `stock` root (quantities
 * are never patched in place — the posting engine owns them, G7), and a movement
 * somebody *else* posted is handed to `onMovement` so W-06 can flash the row and
 * W-07 can prepend it. The user's own writes are filtered out here, so no screen
 * has to remember whose change it is looking at.
 */
export function useRealtimeStock(onMovement?: (event: StockMovementEvent) => void) {
  const { client, companyId, user } = useAslen();
  const qc = useQueryClient();
  // the callback is kept in a ref so a screen that rebuilds it does not
  // resubscribe the channel
  const handler = useRef(onMovement);
  useEffect(() => {
    handler.current = onMovement;
  }, [onMovement]);
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!companyId) return;
    const invalidate = () => {
      void qc.invalidateQueries({ queryKey: qk.stock(companyId) });
    };
    const channel = client
      .channel(`stock-${companyId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'stock_movements', filter: `company_id=eq.${companyId}` }, (payload) => {
        invalidate();
        const row = payload.new as { id?: string; item_id?: string; created_by?: string; no?: string };
        if (!row.created_by || row.created_by === userId) return;
        handler.current?.({ movementId: row.id ?? null, itemId: row.item_id ?? null, actorId: row.created_by, no: row.no ?? null });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'stock_balances', filter: `company_id=eq.${companyId}` }, invalidate)
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [client, companyId, qc, userId]);
}

export function useRealtimeItems() {
  const { client, companyId } = useAslen();
  const qc = useQueryClient();
  useEffect(() => {
    if (!companyId) return;
    const invalidate = () => {
      void qc.invalidateQueries({ queryKey: qk.items(companyId) });
    };
    const channel = client
      .channel(`items-${companyId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'items', filter: `company_id=eq.${companyId}` }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'item_uoms', filter: `company_id=eq.${companyId}` }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'boms', filter: `company_id=eq.${companyId}` }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bom_lines', filter: `company_id=eq.${companyId}` }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bom_operations', filter: `company_id=eq.${companyId}` }, invalidate)
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [client, companyId, qc]);
}

export interface CountLineEvent {
  lineId: string | null;
  countId: string | null;
  actorId: string | null;
}

/**
 * The count half of realtime (`stock_counts` and `stock_count_lines` are both in
 * the publication 20260923000007 sets up). A session the phone is writing into is
 * being watched on the web at the same time — W-09's "Sayım sürüyor" state says
 * so — and the list has to show a submitted count without a reload.
 *
 * Only the count queries are invalidated, not the whole `stock` root: a counter
 * tapping through a shelf would otherwise refetch the matrix, the ledger and
 * every lot once per line. Lines somebody *else* wrote are handed to `onLine` so
 * the review can flash the row.
 */
export function useRealtimeCounts(onLine?: (event: CountLineEvent) => void) {
  const { client, companyId, user } = useAslen();
  const qc = useQueryClient();
  const handler = useRef(onLine);
  useEffect(() => {
    handler.current = onLine;
  }, [onLine]);
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!companyId) return;
    const invalidate = () => {
      void qc.invalidateQueries({ queryKey: qk.counts(companyId) });
      void qc.invalidateQueries({ queryKey: qk.countReviews(companyId) });
      void qc.invalidateQueries({ queryKey: ['stock', companyId, 'count'] });
    };
    const channel = client
      .channel(`counts-${companyId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'stock_counts', filter: `company_id=eq.${companyId}` }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'stock_count_lines', filter: `company_id=eq.${companyId}` }, (payload) => {
        invalidate();
        const row = (payload.new ?? {}) as { id?: string; count_id?: string; counted_by?: string; updated_by?: string };
        const actor = row.counted_by ?? row.updated_by ?? null;
        if (!actor || actor === userId) return;
        handler.current?.({ lineId: row.id ?? null, countId: row.count_id ?? null, actorId: actor });
      })
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [client, companyId, qc, userId]);
}
